import React, { useState, useEffect, useMemo } from 'react';
import Navbar from '../../../globals/components/Navbar';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CircularProgress, 
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Avatar,
  Divider,
  Tooltip,
  IconButton,
  TextField
} from '@mui/material';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  ComposedChart
} from 'recharts';
import { format, parseISO, subDays, startOfDay, endOfDay, isToday, isYesterday, startOfWeek, subWeeks, getYear, getMonth, addDays } from 'date-fns';
import { apiClient } from '../../../globals/utils/api';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

const Analytics = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archives, setArchives] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [weeklyStartDate, setWeeklyStartDate] = useState('');
  const [weeklyEndDate, setWeeklyEndDate] = useState('');
  const [yoyYearA, setYoyYearA] = useState(() => getYear(new Date()));
  const [yoyYearB, setYoyYearB] = useState(() => getYear(new Date()) - 1);
  const [stackByType, setStackByType] = useState(false);
  const [dailyStartDate, setDailyStartDate] = useState('');
  const [dailyEndDate, setDailyEndDate] = useState('');
  const [dailyStackByType, setDailyStackByType] = useState(false);
  const [slabStartDate, setSlabStartDate] = useState('');
  const [slabEndDate, setSlabEndDate] = useState('');
  const [slabStackByType, setSlabStackByType] = useState(false);
  const colors = {
    login: '#4CAF50',
    register: '#2196F3', 
    bond_action: '#FF9800',
    create: '#00C851',
    cancel: '#FF3547',
    endorse: '#9C27B0',
    primary: '#1976d2',
    secondary: '#dc004e',
    gradient: {
      primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      success: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
      warning: 'linear-gradient(135deg, #FF9800 0%, #FFC107 100%)',
      error: 'linear-gradient(135deg, #FF3547 0%, #FF5722 100%)',
      info: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)',
      purple: 'linear-gradient(135deg, #9C27B0 0%, #E91E63 100%)',
      ocean: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      sunset: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      forest: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    }
  };

  const gradientColors = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7'];
  
  const chartColors = {
    logins: '#4CAF50',
    registrations: '#2196F3',
    bondsCreated: '#00C851',
    bondsCancelled: '#FF3547',
    bondsEndorsed: '#2196F3',
    totalActions: '#667eea'
  };

  useEffect(() => {
    fetchDashboardData();
    fetchArchivesData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient('/daily-summary-api-logs/');
      setData(response);
    } catch (err) {
      setError('Failed to fetch dashboard data. Please try again.');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivesData = async () => {
    try {
      setArchivesLoading(true);
      const bonds = await apiClient('/archive-bonds', 'GET');
      setArchives(Array.isArray(bonds) ? bonds : []);
    } catch (err) {
      console.error('Archive bonds fetch error:', err);
    } finally {
      setArchivesLoading(false);
    }
  };

  const currentYear = useMemo(() => getYear(new Date()), []);
  const previousYear = useMemo(() => currentYear - 1, [currentYear]);

  const availableArchiveYears = useMemo(() => {
    const years = new Set();
    for (const b of archives) {
      if (!b?.created_at) continue;
      years.add(getYear(parseISO(b.created_at)));
    }
    const arr = Array.from(years).sort((a, b) => a - b);
    if (arr.length === 0) return [previousYear, currentYear];
    return arr;
  }, [archives, currentYear, previousYear]);

  const weeklyTotalsData = useMemo(() => {
    if (!archives || archives.length === 0) return [];
    // Determine range: default last 12 weeks if not set
    const now = new Date();
    const start = weeklyStartDate ? startOfWeek(parseISO(weeklyStartDate), { weekStartsOn: 1 }) : startOfWeek(subWeeks(now, 11), { weekStartsOn: 1 });
    const end = weeklyEndDate ? startOfWeek(parseISO(weeklyEndDate), { weekStartsOn: 1 }) : startOfWeek(now, { weekStartsOn: 1 });
    const weeks = [];
    let cursor = start;
    while (cursor <= end) {
      weeks.push(cursor);
      cursor = subWeeks(cursor, -1); // add 1 week
    }

    const counts = new Map();
    for (const bond of archives) {
      if (!bond?.created_at) continue;
      const created = parseISO(bond.created_at);
      if (created < start || created > new Date(end.getTime() + 6 * 24 * 60 * 60 * 1000)) {
        // outside of selected window (consider week span)
        continue;
      }
      const monday = startOfWeek(created, { weekStartsOn: 1 });
      const key = format(monday, 'yyyy-MM-dd');
      counts.set(key, (counts.get(key) || 0) + 1);
    }

    return weeks.map((monday) => ({
      week: format(monday, 'MMM d'),
      total: counts.get(format(monday, 'yyyy-MM-dd')) || 0,
    }));
  }, [archives, weeklyStartDate, weeklyEndDate]);

  const bondTypesList = useMemo(() => ['Probate', 'Guardianship', 'Trustee', 'VA', 'Blanket'], []);

  const weeklyStackedData = useMemo(() => {
    if (!archives || archives.length === 0) return [];
    const now = new Date();
    const start = weeklyStartDate ? startOfWeek(parseISO(weeklyStartDate), { weekStartsOn: 1 }) : startOfWeek(subWeeks(now, 11), { weekStartsOn: 1 });
    const end = weeklyEndDate ? startOfWeek(parseISO(weeklyEndDate), { weekStartsOn: 1 }) : startOfWeek(now, { weekStartsOn: 1 });
    const weeks = [];
    let cursor = start;
    while (cursor <= end) {
      weeks.push(cursor);
      cursor = subWeeks(cursor, -1);
    }

    const counts = new Map(); // key: weekKey -> { type -> count }
    for (const bond of archives) {
      if (!bond?.created_at) continue;
      const created = parseISO(bond.created_at);
      if (created < start || created > new Date(end.getTime() + 6 * 24 * 60 * 60 * 1000)) continue;
      const monday = startOfWeek(created, { weekStartsOn: 1 });
      const key = format(monday, 'yyyy-MM-dd');
      const type = bond.bond_type || 'Unknown';
      if (!counts.has(key)) counts.set(key, {});
      const obj = counts.get(key);
      obj[type] = (obj[type] || 0) + 1;
    }

    return weeks.map((monday) => {
      const key = format(monday, 'yyyy-MM-dd');
      const row = { week: format(monday, 'MMM d') };
      const obj = counts.get(key) || {};
      for (const t of bondTypesList) {
        row[t] = obj[t] || 0;
      }
      return row;
    });
  }, [archives, weeklyStartDate, weeklyEndDate, bondTypesList]);

  const dailyTotalsData = useMemo(() => {
    if (!archives || archives.length === 0) return [];
    const now = new Date();
    const start = dailyStartDate ? startOfDay(parseISO(dailyStartDate)) : startOfDay(subDays(now, 29));
    const end = dailyEndDate ? endOfDay(parseISO(dailyEndDate)) : endOfDay(now);

    const counts = new Map(); // key: yyyy-MM-dd -> number
    for (const bond of archives) {
      if (!bond?.created_at) continue;
      const created = parseISO(bond.created_at);
      if (created < start || created > end) continue;
      const key = format(created, 'yyyy-MM-dd');
      counts.set(key, (counts.get(key) || 0) + 1);
    }

    const rows = [];
    let cursor = startOfDay(start);
    const endDay = startOfDay(end);
    while (cursor <= endDay) {
      const key = format(cursor, 'yyyy-MM-dd');
      rows.push({ date: format(cursor, 'MMM d'), total: counts.get(key) || 0 });
      cursor = addDays(cursor, 1);
    }
    return rows;
  }, [archives, dailyStartDate, dailyEndDate]);

  const dailyStackedData = useMemo(() => {
    if (!archives || archives.length === 0) return [];
    const now = new Date();
    const start = dailyStartDate ? startOfDay(parseISO(dailyStartDate)) : startOfDay(subDays(now, 29));
    const end = dailyEndDate ? endOfDay(parseISO(dailyEndDate)) : endOfDay(now);

    const counts = new Map(); // key: yyyy-MM-dd -> { type -> count }
    for (const bond of archives) {
      if (!bond?.created_at) continue;
      const created = parseISO(bond.created_at);
      if (created < start || created > end) continue;
      const key = format(created, 'yyyy-MM-dd');
      const type = bond.bond_type || 'Unknown';
      if (!counts.has(key)) counts.set(key, {});
      const obj = counts.get(key);
      obj[type] = (obj[type] || 0) + 1;
    }

    const rows = [];
    let cursor = startOfDay(start);
    const endDay = startOfDay(end);
    while (cursor <= endDay) {
      const key = format(cursor, 'yyyy-MM-dd');
      const obj = counts.get(key) || {};
      const row = { date: format(cursor, 'MMM d') };
      for (const t of bondTypesList) {
        row[t] = obj[t] || 0;
      }
      rows.push(row);
      cursor = addDays(cursor, 1);
    }
    return rows;
  }, [archives, dailyStartDate, dailyEndDate, bondTypesList]);

  const yoyMonthlyData = useMemo(() => {
    if (!archives || archives.length === 0) return [];
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const yearACounts = new Array(12).fill(0);
    const yearBCounts = new Array(12).fill(0);
    for (const bond of archives) {
      if (!bond?.created_at) continue;
      const d = parseISO(bond.created_at);
      const y = getYear(d);
      const m = getMonth(d);
      if (y === yoyYearA) yearACounts[m] += 1;
      if (y === yoyYearB) yearBCounts[m] += 1;
    }
    return months.map((label, idx) => ({
      month: label,
      [yoyYearB]: yearBCounts[idx],
      [yoyYearA]: yearACounts[idx],
    }));
  }, [archives, yoyYearA, yoyYearB]);

  const slabData = useMemo(() => {
    if (!archives || archives.length === 0) return [];
    const now = new Date();
    const start = slabStartDate ? startOfDay(parseISO(slabStartDate)) : startOfDay(subDays(now, 29));
    const end = slabEndDate ? endOfDay(parseISO(slabEndDate)) : endOfDay(now);

    const rows = [
      { slab: '0-50k', total: 0, Probate: 0, Guardianship: 0, Trustee: 0, VA: 0, Blanket: 0 },
      { slab: '50k-250k', total: 0, Probate: 0, Guardianship: 0, Trustee: 0, VA: 0, Blanket: 0 },
      { slab: '250k-500k', total: 0, Probate: 0, Guardianship: 0, Trustee: 0, VA: 0, Blanket: 0 },
      { slab: '500k+', total: 0, Probate: 0, Guardianship: 0, Trustee: 0, VA: 0, Blanket: 0 },
    ];

    const pickSlabIndex = (amount) => {
      if (amount < 50000) return 0;
      if (amount < 250000) return 1;
      if (amount <= 500000) return 2;
      return 3;
    };

    for (const bond of archives) {
      if (!bond?.created_at) continue;
      const created = parseISO(bond.created_at);
      if (created < start || created > end) continue;
      const amount = parseFloat(bond?.bond_amount ?? bond?.bondAmount ?? 0);
      if (isNaN(amount)) continue;
      const idx = pickSlabIndex(amount);
      const type = bond?.bond_type || 'Unknown';
      rows[idx].total += 1;
      if (type && rows[idx][type] !== undefined) {
        rows[idx][type] += 1;
      }
    }

    return rows;
  }, [archives, slabStartDate, slabEndDate]);

  // Get available date range from data
  const getAvailableDates = () => {
    if (!data.length) return [];
    const dates = [...new Set(data.map(item => item.log_date))].sort();
    return dates;
  };

  // Get today's metrics
  const getTodayMetrics = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const todayData = data.filter(item => item.log_date === today);
    
    const metrics = {
      logins: 0,
      registrations: 0,
      bondsCreated: 0,
      bondsCancelled: 0,
      bondsEndorsed: 0,
      probateBonds: 0,
      guardianshipBonds: 0,
      trusteeBonds: 0,
      vaBonds: 0,
      blanketBonds: 0,
      totalActions: 0
    };

    todayData.forEach(item => {
      metrics.totalActions += item.count;
      
      if (item.category === 'login') {
        metrics.logins += item.count;
      } else if (item.category === 'register') {
        metrics.registrations += item.count;
      } else if (item.category === 'bond_action') {
        if (item.sub_category === 'cancel_bond') {
          metrics.bondsCancelled += item.count;
        } else if (item.sub_category === 'endorse_bond') {
          metrics.bondsEndorsed += item.count;
        } else if (item.sub_category.includes('create') && !item.sub_category.includes('historic')) {
          metrics.bondsCreated += item.count;
          
          if (item.sub_category === 'create_probate_bond') {
            metrics.probateBonds += item.count;
          } else if (item.sub_category === 'create_guardianship_bond') {
            metrics.guardianshipBonds += item.count;
          } else if (item.sub_category === 'create_trustee_bond') {
            metrics.trusteeBonds += item.count;
          } else if (item.sub_category === 'create_va_bond') {
            metrics.vaBonds += item.count;
          } else if (item.sub_category === 'create_blanket_bond') {
            metrics.blanketBonds += item.count;
          }
        }
      }
    });

    return metrics;
  };

  // Get yesterday's metrics for comparison
  const getYesterdayMetrics = () => {
    const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
    const yesterdayData = data.filter(item => item.log_date === yesterday);
    
    return {
      logins: yesterdayData.filter(item => item.category === 'login').reduce((sum, item) => sum + item.count, 0),
      registrations: yesterdayData.filter(item => item.category === 'register').reduce((sum, item) => sum + item.count, 0),
      bondsCreated: yesterdayData.filter(item => 
        item.category === 'bond_action' && item.sub_category.includes('create') && !item.sub_category.includes('historic')
      ).reduce((sum, item) => sum + item.count, 0),
      totalActions: yesterdayData.reduce((sum, item) => sum + item.count, 0)
    };
  };

  // Get daily breakdown for recent days
  const getDailyBreakdown = () => {
    const availableDates = getAvailableDates();
    
    return availableDates.map(date => {
      const dayData = data.filter(item => item.log_date === date);
      const parsedDate = parseISO(date);
      
      const dayMetrics = {
        date: format(parsedDate, 'MMM dd'),
        fullDate: date,
        isToday: isToday(parsedDate),
        isYesterday: isYesterday(parsedDate),
        logins: 0,
        registrations: 0,
        bondsCreated: 0,
        bondsCancelled: 0,
        bondsEndorsed: 0,
        probateBonds: 0,
        guardianshipBonds: 0,
        trusteeBonds: 0,
        vaBonds: 0,
        blanketBonds: 0,
        totalActions: 0
      };

      dayData.forEach(item => {
        dayMetrics.totalActions += item.count;
        
        if (item.category === 'login') {
          dayMetrics.logins += item.count;
        } else if (item.category === 'register') {
          dayMetrics.registrations += item.count;
        } else if (item.category === 'bond_action') {
          if (item.sub_category.includes('create') && !item.sub_category.includes('historic')) {
            dayMetrics.bondsCreated += item.count;
            
            // Track individual bond types
            if (item.sub_category === 'create_probate_bond') {
              dayMetrics.probateBonds += item.count;
            } else if (item.sub_category === 'create_guardianship_bond') {
              dayMetrics.guardianshipBonds += item.count;
            } else if (item.sub_category === 'create_trustee_bond') {
              dayMetrics.trusteeBonds += item.count;
            } else if (item.sub_category === 'create_va_bond') {
              dayMetrics.vaBonds += item.count;
            } else if (item.sub_category === 'create_blanket_bond') {
              dayMetrics.blanketBonds += item.count;
            }
          } else if (item.sub_category === 'cancel_bond') {
            dayMetrics.bondsCancelled += item.count;
          } else if (item.sub_category === 'endorse_bond') {
            dayMetrics.bondsEndorsed += item.count;
          }
        }
      });

      return dayMetrics;
    }).reverse(); // Most recent first
  };

  // Helper function to calculate percentage change
  const getPercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const todayMetrics = getTodayMetrics();
  const yesterdayMetrics = getYesterdayMetrics();
  const dailyBreakdown = getDailyBreakdown();
  const availableDates = getAvailableDates();

  if (loading) {
    return (
      <>
        <Navbar />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress size={60} />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <Box p={4}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <Box p={4} minHeight="80vh" sx={{ 
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        minHeight: '100vh'
      }}>
        {/* Header */}
        <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h3" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
              mb: 1
            }}>
              🚀 Analytics Dashboard
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontSize: '1.1rem'
            }}>
              <AssessmentIcon fontSize="small" />
              Daily Data available for: {availableDates.length > 0 ? `${availableDates[0]} to ${availableDates[availableDates.length - 1]}` : 'No data available'}
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={fetchDashboardData}
              disabled={loading}
              sx={{ 
                background: colors.gradient.primary,
                color: 'white',
                '&:hover': {
                  background: colors.gradient.ocean,
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)'
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Section: Daily Operations */}
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Daily Operations
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Today's Highlights */}
        <Card elevation={3} sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: '12px',
          background: '#ffffff',
          border: '1px solid #e0e0e0'
        }}>
          <Typography variant="h5" gutterBottom sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: '600',
            color: '#333',
            mb: 3
          }}>
            📊 Today's Activity
          </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Avatar sx={{ 
                    bgcolor: colors.login + '20', 
                    mb: 1, 
                    mx: 'auto',
                    width: 48,
                    height: 48
                  }}>
                    <LoginIcon sx={{ fontSize: '1.5rem', color: colors.login }} />
                  </Avatar>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 0.5,
                    color: '#333'
                  }}>
                    {todayMetrics.logins}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>Logins</Typography>
                  {yesterdayMetrics.logins > 0 && (
                    <Typography variant="caption" sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: 0.5,
                      color: getPercentageChange(todayMetrics.logins, yesterdayMetrics.logins) >= 0 ? '#4CAF50' : '#F44336'
                    }}>
                      {getPercentageChange(todayMetrics.logins, yesterdayMetrics.logins) >= 0 ? 
                        <TrendingUpIcon fontSize="small" /> : 
                        <TrendingDownIcon fontSize="small" />}
                      {Math.abs(getPercentageChange(todayMetrics.logins, yesterdayMetrics.logins))}% vs yesterday
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Avatar sx={{ 
                    bgcolor: colors.register + '20', 
                    mb: 1, 
                    mx: 'auto',
                    width: 48,
                    height: 48
                  }}>
                    <PersonAddIcon sx={{ fontSize: '1.5rem', color: colors.register }} />
                  </Avatar>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 0.5,
                    color: '#333'
                  }}>
                    {todayMetrics.registrations}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>Registrations</Typography>
                  {yesterdayMetrics.registrations > 0 && (
                    <Typography variant="caption" sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: 0.5,
                      color: getPercentageChange(todayMetrics.registrations, yesterdayMetrics.registrations) >= 0 ? '#4CAF50' : '#F44336'
                    }}>
                      {getPercentageChange(todayMetrics.registrations, yesterdayMetrics.registrations) >= 0 ? 
                        <TrendingUpIcon fontSize="small" /> : 
                        <TrendingDownIcon fontSize="small" />}
                      {Math.abs(getPercentageChange(todayMetrics.registrations, yesterdayMetrics.registrations))}% vs yesterday
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Avatar sx={{ 
                    bgcolor: colors.create + '20', 
                    mb: 1, 
                    mx: 'auto',
                    width: 48,
                    height: 48
                  }}>
                    <DescriptionIcon sx={{ fontSize: '1.5rem', color: colors.create }} />
                  </Avatar>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 0.5,
                    color: '#333'
                  }}>
                    {todayMetrics.bondsCreated}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>Bonds Created</Typography>
                  {yesterdayMetrics.bondsCreated > 0 && (
                    <Typography variant="caption" sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: 0.5,
                      color: getPercentageChange(todayMetrics.bondsCreated, yesterdayMetrics.bondsCreated) >= 0 ? '#4CAF50' : '#F44336'
                    }}>
                      {getPercentageChange(todayMetrics.bondsCreated, yesterdayMetrics.bondsCreated) >= 0 ? 
                        <TrendingUpIcon fontSize="small" /> : 
                        <TrendingDownIcon fontSize="small" />}
                      {Math.abs(getPercentageChange(todayMetrics.bondsCreated, yesterdayMetrics.bondsCreated))}% vs yesterday
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Avatar sx={{ 
                    bgcolor: colors.cancel + '20', 
                    mb: 1, 
                    mx: 'auto',
                    width: 48,
                    height: 48
                  }}>
                    <CancelIcon sx={{ fontSize: '1.5rem', color: colors.cancel }} />
                  </Avatar>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 0.5,
                    color: '#333'
                  }}>
                    {todayMetrics.bondsCancelled}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>Cancellations</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Avatar sx={{ 
                    bgcolor: '#2196F320', 
                    mb: 1, 
                    mx: 'auto',
                    width: 48,
                    height: 48
                  }}>
                    <EditIcon sx={{ fontSize: '1.5rem', color: '#2196F3' }} />
                  </Avatar>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 0.5,
                    color: '#333'
                  }}>
                    {todayMetrics.bondsEndorsed}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>Endorsements</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box textAlign="center">
                  <Avatar sx={{ 
                    bgcolor: colors.primary + '20', 
                    mb: 1, 
                    mx: 'auto',
                    width: 48,
                    height: 48
                  }}>
                    <ShowChartIcon sx={{ fontSize: '1.5rem', color: colors.primary }} />
                  </Avatar>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 0.5,
                    color: '#333'
                  }}>
                    {todayMetrics.totalActions}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>Total Actions</Typography>
                  {yesterdayMetrics.totalActions > 0 && (
                    <Typography variant="caption" sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: 0.5,
                      color: getPercentageChange(todayMetrics.totalActions, yesterdayMetrics.totalActions) >= 0 ? '#4CAF50' : '#F44336'
                    }}>
                      {getPercentageChange(todayMetrics.totalActions, yesterdayMetrics.totalActions) >= 0 ? 
                        <TrendingUpIcon fontSize="small" /> : 
                        <TrendingDownIcon fontSize="small" />}
                      {Math.abs(getPercentageChange(todayMetrics.totalActions, yesterdayMetrics.totalActions))}% vs yesterday
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
        </Card>

        <Card sx={{ 
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          mb: 4,
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              fontWeight: 'bold',
              color: '#333',
              mb: 3
            }}>
              Daily Activity Breakdown
              <AssessmentIcon sx={{ color: '#666' }} />
            </Typography>
              <Grid container spacing={3}>
                {dailyBreakdown.slice(0, 4).map((day, index) => (
                  <Grid item xs={12} sm={6} md={3} key={day.fullDate}>
                    <Paper 
                      elevation={day.isToday ? 12 : 4} 
                      sx={{ 
                        p: 3, 
                        borderRadius: '16px',
                        background: day.isToday ? 
                          colors.gradient.primary : 
                          day.isYesterday ? colors.gradient.ocean : 
                          'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        color: (day.isToday || day.isYesterday) ? 'white' : 'inherit',
                        transform: day.isToday ? 'scale(1.02)' : 'scale(1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: day.isToday ? 'scale(1.04)' : 'scale(1.02)',
                          boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                        },
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': day.isToday ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                          pointerEvents: 'none'
                        } : {}
                      }}
                    >
                        <Typography variant="h6" gutterBottom sx={{ 
                          fontWeight: 'bold',
                          textAlign: 'center',
                          mb: 2,
                          textShadow: (day.isToday || day.isYesterday) ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none'
                        }}>
                          {day.date} 
                          {day.isToday && <Chip 
                            label="TODAY" 
                            size="small" 
                            sx={{ 
                              ml: 1, 
                              bgcolor: 'rgba(255,255,255,0.2)', 
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }} 
                          />}
                          {day.isYesterday && <Chip 
                            label="YESTERDAY" 
                            size="small" 
                            sx={{ 
                              ml: 1, 
                              bgcolor: 'rgba(255,255,255,0.2)', 
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }} 
                          />}
        </Typography>
                        <Divider sx={{ 
                          my: 2, 
                          bgcolor: (day.isToday || day.isYesterday) ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)',
                          height: 2
                        }} />
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LoginIcon fontSize="small" />
                            <Typography variant="body2" fontWeight="500">Logins:</Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">{day.logins}</Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonAddIcon fontSize="small" />
                            <Typography variant="body2" fontWeight="500">Registrations:</Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">{day.registrations}</Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <DescriptionIcon fontSize="small" />
                            <Typography variant="body2" fontWeight="500">Bonds Created:</Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">{day.bondsCreated}</Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CancelIcon fontSize="small" />
                            <Typography variant="body2" fontWeight="500">Cancellations:</Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">{day.bondsCancelled}</Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EditIcon fontSize="small" />
                            <Typography variant="body2" fontWeight="500">Endorsements:</Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">{day.bondsEndorsed}</Typography>
                        </Box>
                        
                        <Divider sx={{ 
                          my: 2, 
                          bgcolor: (day.isToday || day.isYesterday) ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)',
                          height: 2
                        }} />
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            <ShowChartIcon fontSize="small" />
                            <Typography variant="body1" fontWeight="600">Total Actions:</Typography>
                          </Box>
                          <Typography variant="h5" fontWeight="bold" sx={{
                            textShadow: (day.isToday || day.isYesterday) ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none'
                          }}>
                            {day.totalActions}
                          </Typography>
                        </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
        </Card>

        

        {/* Bond Type Breakdown */}
        {todayMetrics.bondsCreated > 0 && (
          <Card sx={{ mb: 4, overflow: 'visible' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📊 Today's Bond Creation Breakdown
              </Typography>
              <Grid container spacing={2}>
                {todayMetrics.probateBonds > 0 && (
                  <Grid item xs={6} sm={2.4}>
                    <Chip 
                      label={`Probate: ${todayMetrics.probateBonds}`}
                      sx={{ 
                        bgcolor: gradientColors[0] + '20', 
                        color: gradientColors[0],
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        width: '100%'
                      }}
                    />
                  </Grid>
                )}
                {todayMetrics.guardianshipBonds > 0 && (
                  <Grid item xs={6} sm={2.4}>
                    <Chip 
                      label={`Guardianship: ${todayMetrics.guardianshipBonds}`}
                      sx={{ 
                        bgcolor: gradientColors[1] + '20', 
                        color: gradientColors[1],
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        width: '100%'
                      }}
                    />
                  </Grid>
                )}
                {todayMetrics.trusteeBonds > 0 && (
                  <Grid item xs={6} sm={2.4}>
                    <Chip 
                      label={`Trustee: ${todayMetrics.trusteeBonds}`}
                      sx={{ 
                        bgcolor: gradientColors[2] + '20', 
                        color: gradientColors[2],
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        width: '100%'
                      }}
                    />
                  </Grid>
                )}
                {todayMetrics.vaBonds > 0 && (
                  <Grid item xs={6} sm={2.4}>
                    <Chip 
                      label={`VA: ${todayMetrics.vaBonds}`}
                      sx={{ 
                        bgcolor: gradientColors[3] + '20', 
                        color: gradientColors[3],
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        width: '100%'
                      }}
                    />
                  </Grid>
                )}
                {todayMetrics.blanketBonds > 0 && (
                  <Grid item xs={6} sm={2.4}>
                    <Chip 
                      label={`Blanket: ${todayMetrics.blanketBonds}`}
                      sx={{ 
                        bgcolor: gradientColors[4] + '20', 
                        color: gradientColors[4],
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        width: '100%'
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        

        

        {/* Separate Charts Section */}
          {/* Bond Creation by Type - Full Width */}
          <Card sx={{ 
            mb: 4,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                fontWeight: 'bold',
                color: '#333',
                mb: 3
              }}>
                Recent Daily Bond Creation by Type
                <BarChartIcon sx={{ color: '#666' }} />
              </Typography>
              <Box sx={{ 
                background: '#ffffff', 
                borderRadius: '12px', 
                p: 2,
                border: '1px solid #f0f0f0'
              }}>
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart data={dailyBreakdown} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <defs>
                      <linearGradient id="probateGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={gradientColors[0]} stopOpacity={1}/>
                        <stop offset="95%" stopColor={gradientColors[0]} stopOpacity={0.8}/>
                      </linearGradient>
                      <linearGradient id="guardianshipGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={gradientColors[1]} stopOpacity={1}/>
                        <stop offset="95%" stopColor={gradientColors[1]} stopOpacity={0.8}/>
                      </linearGradient>
                      <linearGradient id="trusteeGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={gradientColors[2]} stopOpacity={1}/>
                        <stop offset="95%" stopColor={gradientColors[2]} stopOpacity={0.8}/>
                      </linearGradient>
                      <linearGradient id="vaGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={gradientColors[3]} stopOpacity={1}/>
                        <stop offset="95%" stopColor={gradientColors[3]} stopOpacity={0.8}/>
                      </linearGradient>
                      <linearGradient id="blanketGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={gradientColors[4]} stopOpacity={1}/>
                        <stop offset="95%" stopColor={gradientColors[4]} stopOpacity={0.8}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid 
                      strokeDasharray="2 2" 
                      stroke="#e3f2fd" 
                      strokeOpacity={0.6}
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }}
                      tickLine={{ stroke: '#ddd', strokeWidth: 1 }}
                      axisLine={{ stroke: '#ddd', strokeWidth: 1 }}
                      tickMargin={8}
                    />
                    <YAxis 
                      tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }}
                      tickLine={{ stroke: '#ddd', strokeWidth: 1 }}
                      axisLine={{ stroke: '#ddd', strokeWidth: 1 }}
                      allowDecimals={false}
                      tickMargin={5}
                    />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: 'none',
                        borderRadius: '16px',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                        fontSize: '14px',
                        fontWeight: 500,
                        padding: '12px 16px'
                      }}
                      cursor={{ 
                        fill: 'rgba(102, 126, 234, 0.08)', 
                        stroke: 'rgba(102, 126, 234, 0.3)',
                        strokeWidth: 2,
                        strokeDasharray: '5 5'
                      }}
                      labelStyle={{ color: '#333', fontWeight: 'bold', marginBottom: '8px' }}
                    />
                    <Legend 
                      wrapperStyle={{ 
                        paddingTop: '20px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                      iconType="rect"
                    />
                    <Bar 
                      dataKey="probateBonds" 
                      fill="url(#probateGradient)"
                      name="Probate Bonds"
                      radius={[4, 4, 0, 0]}
                      stackId="bonds"
                    />
                    <Bar 
                      dataKey="guardianshipBonds" 
                      fill="url(#guardianshipGradient)"
                      name="Guardianship Bonds"
                      radius={[4, 4, 0, 0]}
                      stackId="bonds"
                    />
                    <Bar 
                      dataKey="trusteeBonds" 
                      fill="url(#trusteeGradient)"
                      name="Trustee Bonds"
                      radius={[4, 4, 0, 0]}
                      stackId="bonds"
                    />
                    <Bar 
                      dataKey="vaBonds" 
                      fill="url(#vaGradient)"
                      name="VA Bonds"
                      radius={[4, 4, 0, 0]}
                      stackId="bonds"
                    />
                    <Bar 
                      dataKey="blanketBonds" 
                      fill="url(#blanketGradient)"
                      name="Blanket Bonds"
                      radius={[4, 4, 0, 0]}
                      stackId="bonds"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

                    {/* Login/Registration and Cancellations/Endorsements - Side by Side */}
                    <Grid container spacing={4} mb={4}>
            {/* 1. Login & Registration Trends */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                height: '100%',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    fontWeight: 'bold',
                    color: '#333',
                    mb: 3
                  }}>
                    User Activity Trends
                    <PeopleIcon sx={{ color: '#666' }} />
                  </Typography>
                  <Box sx={{ 
                    background: '#ffffff', 
                    borderRadius: '12px', 
                    p: 2,
                    border: '1px solid #f0f0f0'
                  }}>
                    <ResponsiveContainer width="100%" height={320}>
                      <ComposedChart data={dailyBreakdown} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <defs>
                          <linearGradient id="loginAreaGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.9}/>
                            <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="registrationBarGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2196F3" stopOpacity={1}/>
                            <stop offset="95%" stopColor="#64B5F6" stopOpacity={0.8}/>
                          </linearGradient>
                          <filter id="glow">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge> 
                              <feMergeNode in="coloredBlur"/>
                              <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                          </filter>
                        </defs>
                        <CartesianGrid 
                          strokeDasharray="2 2" 
                          stroke="#e3f2fd" 
                          strokeOpacity={0.6}
                          horizontal={true}
                          vertical={false}
                        />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }}
                          tickLine={{ stroke: '#ddd', strokeWidth: 1 }}
                          axisLine={{ stroke: '#ddd', strokeWidth: 1 }}
                          tickMargin={8}
                        />
                        <YAxis 
                          tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }}
                          tickLine={{ stroke: '#ddd', strokeWidth: 1 }}
                          axisLine={{ stroke: '#ddd', strokeWidth: 1 }}
                          allowDecimals={false}
                          tickMargin={5}
                        />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: 'none',
                            borderRadius: '16px',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                            fontSize: '14px',
                            fontWeight: 500,
                            padding: '12px 16px'
                          }}
                          cursor={{ 
                            fill: 'rgba(102, 126, 234, 0.08)', 
                            stroke: 'rgba(102, 126, 234, 0.3)',
                            strokeWidth: 2,
                            strokeDasharray: '5 5'
                          }}
                          labelStyle={{ color: '#333', fontWeight: 'bold', marginBottom: '8px' }}
                        />
                        <Legend 
                          wrapperStyle={{ 
                            paddingTop: '20px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          iconType="rect"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="logins" 
                          fill="url(#loginAreaGradient)"
                          stroke="#4CAF50"
                          strokeWidth={4}
                          name="Daily Logins"
                          dot={{ 
                            fill: '#4CAF50', 
                            strokeWidth: 3, 
                            stroke: '#fff',
                            r: 6,
                            filter: 'url(#glow)'
                          }}
                          activeDot={{ 
                            r: 8, 
                            stroke: '#4CAF50', 
                            strokeWidth: 4,
                            fill: '#fff',
                            filter: 'url(#glow)'
                          }}
                        />
                        <Bar 
                          dataKey="registrations" 
                          fill="url(#registrationBarGradient)"
                          name="New Registrations"
                          radius={[8, 8, 0, 0]}
                          maxBarSize={40}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* 2. Cancellations vs Endorsements */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                height: '100%',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    fontWeight: 'bold',
                    color: '#333',
                    mb: 3
                  }}>
                    Bond Actions Comparison
                    <CompareArrowsIcon sx={{ color: '#666' }} />
                  </Typography>
                  <Box sx={{ 
                    background: '#ffffff', 
                    borderRadius: '12px', 
                    p: 2,
                    border: '1px solid #f0f0f0'
                  }}>
                    <ResponsiveContainer width="100%" height={320}>
                      <ComposedChart data={dailyBreakdown} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <defs>
                          <linearGradient id="endorsementAreaGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={chartColors.bondsEndorsed} stopOpacity={0.9}/>
                            <stop offset="95%" stopColor={chartColors.bondsEndorsed} stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="cancellationAreaGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={chartColors.bondsCancelled} stopOpacity={0.9}/>
                            <stop offset="95%" stopColor={chartColors.bondsCancelled} stopOpacity={0.1}/>
                          </linearGradient>
                          <filter id="glowEndorse">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge> 
                              <feMergeNode in="coloredBlur"/>
                              <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                          </filter>
                        </defs>
                        <CartesianGrid 
                          strokeDasharray="2 2" 
                          stroke="#e3f2fd" 
                          strokeOpacity={0.6}
                          horizontal={true}
                          vertical={false}
                        />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }}
                          tickLine={{ stroke: '#ddd', strokeWidth: 1 }}
                          axisLine={{ stroke: '#ddd', strokeWidth: 1 }}
                          tickMargin={8}
                        />
                        <YAxis 
                          tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }}
                          tickLine={{ stroke: '#ddd', strokeWidth: 1 }}
                          axisLine={{ stroke: '#ddd', strokeWidth: 1 }}
                          allowDecimals={false}
                          tickMargin={5}
                        />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: 'none',
                            borderRadius: '16px',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                            fontSize: '14px',
                            fontWeight: 500,
                            padding: '12px 16px'
                          }}
                          cursor={{ 
                            fill: 'rgba(102, 126, 234, 0.08)', 
                            stroke: 'rgba(102, 126, 234, 0.3)',
                            strokeWidth: 2,
                            strokeDasharray: '5 5'
                          }}
                          labelStyle={{ color: '#333', fontWeight: 'bold', marginBottom: '8px' }}
                        />
                        <Legend 
                          wrapperStyle={{ 
                            paddingTop: '20px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          iconType="circle"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="bondsEndorsed" 
                          fill="url(#endorsementAreaGradient)"
                          stroke={chartColors.bondsEndorsed}
                          strokeWidth={4}
                          name="Endorsements"
                          dot={{ 
                            fill: chartColors.bondsEndorsed, 
                            strokeWidth: 3, 
                            stroke: '#fff',
                            r: 6,
                            filter: 'url(#glowEndorse)'
                          }}
                          activeDot={{ 
                            r: 8, 
                            stroke: chartColors.bondsEndorsed, 
                            strokeWidth: 4,
                            fill: '#fff',
                            filter: 'url(#glowEndorse)'
                          }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="bondsCancelled" 
                          fill="url(#cancellationAreaGradient)"
                          stroke={chartColors.bondsCancelled}
                          strokeWidth={4}
                          name="Cancellations"
                          dot={{ 
                            fill: chartColors.bondsCancelled, 
                            strokeWidth: 3, 
                            stroke: '#fff',
                            r: 6
                          }}
                          activeDot={{ 
                            r: 8, 
                            stroke: chartColors.bondsCancelled, 
                            strokeWidth: 4,
                            fill: '#fff'
                          }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Section: Historical Reporting */}
          <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
            Historical Reporting
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Weekly Totals and Year-over-Year Comparisons */}
          <Grid container spacing={4} mb={4}>
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                height: '100%',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    fontWeight: 'bold',
                    color: '#333',
                    mb: 3
                  }}>
                    Weekly Totals (Last 12 Weeks)
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={4} lg={5}>
                      <TextField
                        fullWidth
                        label="Week Range Start"
                        type="date"
                        value={weeklyStartDate}
                        onChange={(e) => setWeeklyStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={5}>
                      <TextField
                        fullWidth
                        label="Week Range End"
                        type="date"
                        value={weeklyEndDate}
                        onChange={(e) => setWeeklyEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} lg={2} display="flex" alignItems="center">
                      {archivesLoading && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">Loading...</Typography>
                        </Box>
                      )}
                      <FormControlLabel
                        sx={{ ml: 'auto' }}
                        control={<Switch checked={stackByType} onChange={(e) => setStackByType(e.target.checked)} />}
                        label="Stack by type"
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ 
                    background: '#ffffff', 
                    borderRadius: '12px', 
                    p: 2,
                    border: '1px solid #f0f0f0'
                  }}>
                    {archivesLoading ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={stackByType ? weeklyStackedData : weeklyTotalsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="2 2" stroke="#e3f2fd" strokeOpacity={0.6} horizontal={true} vertical={false} />
                          <XAxis dataKey="week" tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} tickMargin={8} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} />
                          <RechartsTooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', fontSize: '14px', fontWeight: 500, padding: '12px 16px' }} />
                          <Legend />
                          {stackByType ? (
                            <>
                              <Bar dataKey="Probate" stackId="b" name="Probate" fill="#5B8FF9" radius={[6, 6, 0, 0]} maxBarSize={40} />
                              <Bar dataKey="Guardianship" stackId="b" name="Guardianship" fill="#61DDAA" radius={[6, 6, 0, 0]} maxBarSize={40} />
                              <Bar dataKey="Trustee" stackId="b" name="Trustee" fill="#65789B" radius={[6, 6, 0, 0]} maxBarSize={40} />
                              <Bar dataKey="VA" stackId="b" name="VA" fill="#F6BD16" radius={[6, 6, 0, 0]} maxBarSize={40} />
                              <Bar dataKey="Blanket" stackId="b" name="Blanket" fill="#7262fd" radius={[6, 6, 0, 0]} maxBarSize={40} />
                            </>
                          ) : (
                            <Bar dataKey="total" name="Bonds" fill="#1976d2" radius={[6, 6, 0, 0]} maxBarSize={40} />
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                height: '100%',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    fontWeight: 'bold',
                    color: '#333',
                    mb: 3
                  }}>
                    Year-over-Year (Monthly)
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="yoy-year-a-label">Current Year</InputLabel>
                        <Select
                          labelId="yoy-year-a-label"
                          label="Current Year"
                          value={yoyYearA}
                          onChange={(e) => setYoyYearA(e.target.value)}
                        >
                          {availableArchiveYears.map((y) => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="yoy-year-b-label">Comparison Year</InputLabel>
                        <Select
                          labelId="yoy-year-b-label"
                          label="Comparison Year"
                          value={yoyYearB}
                          onChange={(e) => setYoyYearB(e.target.value)}
                        >
                          {availableArchiveYears.map((y) => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box sx={{ 
                    background: '#ffffff', 
                    borderRadius: '12px', 
                    p: 2,
                    border: '1px solid #f0f0f0'
                  }}>
                    {archivesLoading ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={yoyMonthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="2 2" stroke="#e3f2fd" strokeOpacity={0.6} horizontal={true} vertical={false} />
                          <XAxis dataKey="month" tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} tickMargin={8} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} />
                          <RechartsTooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', fontSize: '14px', fontWeight: 500, padding: '12px 16px' }} />
                          <Legend />
                          <Line type="monotone" dataKey={String(yoyYearB)} name={String(yoyYearB)} stroke="#9c27b0" strokeWidth={3} dot={false} />
                          <Line type="monotone" dataKey={String(yoyYearA)} name={String(yoyYearA)} stroke="#2e7d32" strokeWidth={3} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>




        {/* Daily Totals (70%) + Bonds by Amount Slab (30%) */}
        <Grid container spacing={4} mb={4} mt={0} alignItems="stretch">
          <Grid item xs={12} md={7} lg={7} xl={10}>
            <Card sx={{ 
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  fontWeight: 'bold',
                  color: '#333',
                  mb: 3
                }}>
                  Daily Totals (Archives)
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={4} lg={5}>
                    <TextField
                      fullWidth
                      label="Date Range Start"
                      type="date"
                      value={dailyStartDate}
                      onChange={(e) => setDailyStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={5}>
                    <TextField
                      fullWidth
                      label="Date Range End"
                      type="date"
                      value={dailyEndDate}
                      onChange={(e) => setDailyEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={2} display="flex" alignItems="center">
                    {archivesLoading && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading...</Typography>
                      </Box>
                    )}
                    <FormControlLabel
                      sx={{ ml: 'auto' }}
                      control={<Switch checked={dailyStackByType} onChange={(e) => setDailyStackByType(e.target.checked)} />}
                      label="Stack by type"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ 
                  background: '#ffffff', 
                  borderRadius: '12px', 
                  p: 2,
                  border: '1px solid #f0f0f0'
                }}>
                  {archivesLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      {dailyStackByType ? (
                        <BarChart data={dailyStackedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="2 2" stroke="#e3f2fd" strokeOpacity={0.6} horizontal={true} vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} tickMargin={8} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} />
                          <RechartsTooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', fontSize: '14px', fontWeight: 500, padding: '12px 16px' }} />
                          <Legend />
                          <Bar dataKey="Probate" stackId="d" name="Probate" fill="#5B8FF9" radius={[6, 6, 0, 0]} maxBarSize={40} />
                          <Bar dataKey="Guardianship" stackId="d" name="Guardianship" fill="#61DDAA" radius={[6, 6, 0, 0]} maxBarSize={40} />
                          <Bar dataKey="Trustee" stackId="d" name="Trustee" fill="#65789B" radius={[6, 6, 0, 0]} maxBarSize={40} />
                          <Bar dataKey="VA" stackId="d" name="VA" fill="#F6BD16" radius={[6, 6, 0, 0]} maxBarSize={40} />
                          <Bar dataKey="Blanket" stackId="d" name="Blanket" fill="#7262fd" radius={[6, 6, 0, 0]} maxBarSize={40} />
                        </BarChart>
                      ) : (
                        <LineChart data={dailyTotalsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="2 2" stroke="#e3f2fd" strokeOpacity={0.6} horizontal={true} vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} tickMargin={8} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} />
                          <RechartsTooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', fontSize: '14px', fontWeight: 500, padding: '12px 16px' }} />
                          <Legend />
                          <Line type="monotone" dataKey="total" name="Total" stroke="#1976d2" strokeWidth={3} dot={false} />
                        </LineChart>
                      )}
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5} lg={5} xl={2}>
            <Card sx={{ 
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  fontWeight: 'bold',
                  color: '#333',
                  mb: 3
                }}>
                  Bonds by Amount Slab
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Date Range Start"
                      type="date"
                      value={slabStartDate}
                      onChange={(e) => setSlabStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Date Range End"
                      type="date"
                      value={slabEndDate}
                      onChange={(e) => setSlabEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} display="flex" alignItems="center" justifyContent="flex-end">
                    <FormControlLabel
                      control={<Switch checked={slabStackByType} onChange={(e) => setSlabStackByType(e.target.checked)} />}
                      label="Stack by type"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ 
                  background: '#ffffff', 
                  borderRadius: '12px', 
                  p: 2,
                  border: '1px solid #f0f0f0'
                }}>
                  {archivesLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={slabData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="2 2" stroke="#e3f2fd" strokeOpacity={0.6} horizontal={true} vertical={false} />
                        <XAxis dataKey="slab" tick={{ fontSize: 12, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} tickMargin={6} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#555', fontWeight: 500 }} tickLine={{ stroke: '#ddd', strokeWidth: 1 }} axisLine={{ stroke: '#ddd', strokeWidth: 1 }} />
                        <RechartsTooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', fontSize: '13px', fontWeight: 500, padding: '10px 12px' }} />
                        {slabStackByType ? (
                          <>
                            <Legend wrapperStyle={{ fontSize: '12px' }} iconSize={10} />
                            <Bar dataKey="Probate" stackId="slab" name="Probate" fill="#5B8FF9" radius={[6, 6, 0, 0]} maxBarSize={40} />
                            <Bar dataKey="Guardianship" stackId="slab" name="Guardianship" fill="#61DDAA" radius={[6, 6, 0, 0]} maxBarSize={40} />
                            <Bar dataKey="Trustee" stackId="slab" name="Trustee" fill="#65789B" radius={[6, 6, 0, 0]} maxBarSize={40} />
                            <Bar dataKey="VA" stackId="slab" name="VA" fill="#F6BD16" radius={[6, 6, 0, 0]} maxBarSize={40} />
                            <Bar dataKey="Blanket" stackId="slab" name="Blanket" fill="#7262fd" radius={[6, 6, 0, 0]} maxBarSize={40} />
                          </>
                        ) : (
                          <Bar dataKey="total" name="Bonds" fill="#0ea5e9" radius={[6, 6, 0, 0]} maxBarSize={40} />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      </Box>


      <Box p={4} minHeight="80vh" bgcolor="#f9fafb">
        <Typography variant="h4" gutterBottom>
          Power BI Dashboard
        </Typography>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '80vh',
          }}
        >
          <iframe
            title="Vermost Portal Analytics"
            width="100%"
            height="100%"
            src="https://app.powerbi.com/reportEmbed?reportId=a203dc51-6600-463b-ac15-db1418019e9f&autoAuth=true&ctid=eb8172ed-a7a8-432c-b262-0327e4eb2097"
            frameBorder="0"
            allowFullScreen={true}
          ></iframe>
        </Box>
      </Box>

    </>
  );
};

export default Analytics;
